/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    FMH_NPI,
    FMH_UNMANAGED_NPI,
    NetworkStatusDisplayTextMap,
    NetworkStatuses,
} from '@strata/constants';
import { conditionalString, formatNameLastFirst, sortAsc } from '@strata/helper-functions';

import { REFERENCE_API_URL } from 'api/APIBase';
import { UNSPECIFIED_NPI } from 'services/constants';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

/** Adds status suffixes to supplier dropdown names (network status; optional no-market). */
const getSupplierDropdownDisplayName = (name, networkStatus, noMarket = false) => {
    const suffixes = [
        networkStatus !== NetworkStatuses.ACTIVE && NetworkStatusDisplayTextMap[networkStatus],
        noMarket && 'No Live Referrals',
    ].filter(Boolean);
    return `${name}${conditionalString(suffixes.length, ` (${suffixes.join(', ')})`)}`;
};

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        /** Gets list of payers */
        getPayers: build.query({
            query: () => 'payers/',
            transformResponse: (response) => {
                const payerList = response
                    .map((payer) => ({
                        text: payer.payer_name,
                        name: payer.payer_name,
                        value: payer.payer_id,
                        secondaryData: payer.payer_id,
                        ...payer,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const payerMap = Object.fromEntries(
                    (payerList ?? []).map((payer) => [payer.payer_id, payer]),
                );
                return { payerList, payerMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving health plans.',
            ),
        }),
        /** Gets list of payer segments */
        getPayerSegments: build.query({
            query: () => 'payer_segments',
            transformResponse: (response) => {
                const payerSegmentList = response
                    .map((segment) => ({
                        name: segment.segment_name,
                        secondaryData: segment.segment_id,
                        payerId: segment.payer_id,
                        ...segment,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const payerSegmentMap = Object.fromEntries(
                    (payerSegmentList ?? []).map((segment) => [segment.segment_id, segment]),
                );
                return { payerSegmentList, payerSegmentMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving health plan segments.',
            ),
        }),
        /** Gets list of all pharmacies for use with Out of Network Component. */
        getAllSuppliers: build.query({
            query: () => '/suppliers/parents',
            transformResponse: (response) => {
                let supplierList = [
                    ...(response ?? []).map((supplier) => ({
                        npi: supplier.supplier_npi,
                        name: supplier.organization_name ?? 'Unknown Supplier',
                        networkStatus: supplier.network_status,
                        secondaryData: supplier.supplier_npi,
                    })),
                    {
                        npi: FMH_NPI,
                        name: 'Free Market Health',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: FMH_NPI,
                    },
                    {
                        npi: FMH_UNMANAGED_NPI,
                        name: 'Unmanaged',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: FMH_UNMANAGED_NPI,
                    },
                    {
                        npi: UNSPECIFIED_NPI,
                        name: 'Unspecified',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: UNSPECIFIED_NPI,
                    },
                ].sort((a, b) => sortAsc(a.name, b.name));
                const supplierMap = Object.fromEntries(
                    supplierList.map(({ npi, name, networkStatus }) => [
                        npi,
                        { name, networkStatus },
                    ]),
                );
                // Add network status display text to non-active supplier options for dropdown
                supplierList = supplierList.map((supplier) => ({
                    ...supplier,
                    name: getSupplierDropdownDisplayName(supplier.name, supplier.networkStatus),
                }));
                return { supplierList, supplierMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving all suppliers.',
            ),
        }),
        /** Gets list of all pharmacies for use with Out of Network Component. */
        getAllSuppliersWithChildren: build.query({
            query: () => '/suppliers/',
            transformResponse: (response) => {
                let supplierList = [
                    ...(response ?? []).map((supplier) => ({
                        npi: supplier.supplier_npi,
                        name: supplier.organization_name ?? 'Unknown Supplier',
                        networkStatus: supplier.network_status,
                        secondaryData: supplier.supplier_npi,
                        children: [],
                        parentSupplierNpi: supplier.parent_supplier_npi,
                    })),
                    {
                        npi: FMH_NPI,
                        name: 'Free Market Health',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: FMH_NPI,
                        children: [],
                        parentSupplierNpi: null,
                    },
                    {
                        npi: FMH_UNMANAGED_NPI,
                        name: 'Unmanaged',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: FMH_UNMANAGED_NPI,
                        children: [],
                        parentSupplierNpi: null,
                    },
                    {
                        npi: UNSPECIFIED_NPI,
                        name: 'Unspecified',
                        networkStatus: NetworkStatuses.ACTIVE,
                        secondaryData: UNSPECIFIED_NPI,
                        children: [],
                        parentSupplierNpi: null,
                    },
                ].sort((a, b) => sortAsc(a.name, b.name));

                // Build map
                const supplierMap = Object.fromEntries(
                    supplierList.map(
                        ({ npi, name, networkStatus, parentSupplierNpi, children }) => [
                            npi,
                            { name, networkStatus, parentSupplierNpi, children },
                        ],
                    ),
                );

                // Add children to the map
                supplierList.forEach((supplier) => {
                    if (!supplierMap[supplier.parentSupplierNpi]) return;

                    supplierMap[supplier.parentSupplierNpi].children.push(supplier.npi);
                });

                // Add network status display text to non-active supplier options for dropdown
                supplierList = supplierList.map((supplier) => ({
                    ...supplier,
                    name: getSupplierDropdownDisplayName(supplier.name, supplier.networkStatus),
                }));
                return { supplierList, supplierMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving all suppliers.',
            ),
        }),
        /** Gets list of pharmacies eligible to participate in a Live Referral. */
        getEligibleSuppliers: build.query({
            query: ({
                payerId,
                segmentId,
                memberState,
                productServiceOid,
                benefit,
                placeOfService,
                isBrand,
                lineOfBusiness,
                lobSegment,
            }) => ({
                url: '/eligible_suppliers/',
                params: {
                    payer_id: payerId,
                    segment_id: segmentId,
                    member_state: memberState,
                    product_service_oid: productServiceOid,
                    benefit,
                    place_of_service: placeOfService,
                    is_brand: isBrand,
                    line_of_business: lineOfBusiness,
                    lob_segment: lobSegment,
                },
            }),
            transformResponse: (response) =>
                (response ?? []).map((supplier) => ({
                    npi: supplier.npi,
                    name: getSupplierDropdownDisplayName(
                        supplier.organization_name,
                        supplier.network_status,
                        supplier.no_market,
                    ),
                    secondaryData: supplier.npi,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving eligible suppliers.',
            ),
        }),
        getSupplierDetails: build.query({
            // eslint-disable-next-line complexity
            queryFn: async ({ supplierNpi }, _queryApi, _extraArgs, baseQuery) => {
                if (supplierNpi === FMH_NPI) {
                    return {
                        data: {
                            supplierNpi: FMH_NPI,
                            organizationName: 'Free Market Health',
                            networkStatus: NetworkStatuses.ACTIVE,
                        },
                    };
                }
                if (supplierNpi === FMH_UNMANAGED_NPI) {
                    return {
                        data: {
                            supplierNpi: FMH_UNMANAGED_NPI,
                            organizationName: 'Unmanaged',
                            networkStatus: NetworkStatuses.ACTIVE,
                        },
                    };
                }

                const response = await baseQuery(`suppliers/${supplierNpi}`);

                if (response?.error && response.error.status !== 404) {
                    handleError('An error occurred retrieving pharmacy details.', response.error);
                    return response;
                }

                return {
                    data: {
                        supplierNpi: response?.data?.supplier_npi,
                        organizationName: response?.data?.organization_name,
                        address1: response?.data?.address_1,
                        address2: response?.data?.address_2,
                        city: response?.data?.city,
                        state: response?.data?.state,
                        zip: response?.data?.postal_code,
                        fax: response?.data?.fax_number,
                        phone: response?.data?.telephone_number,
                        networkStatus: response?.data?.network_status,
                        integrationAvailable: response?.data?.integration_available,
                        allowMedical: response?.data?.allow_medical,
                        sendOnOption: response?.data?.send_on_option,
                        childrenNpis: response?.data?.children_npis,
                    },
                };
            },
        }),
        getPrescriberDetails: build.query({
            query: ({ prescriberNpi }) => `prescribers/${prescriberNpi}`,
            transformResponse: (response) => ({
                prescriberName: formatNameLastFirst(response.last_name, response.first_name),
                prescriberPhone: response.telephone_number,
                prescriberFax: response.fax_number,
                pointOfContactName: formatNameLastFirst(
                    response.point_of_contact_last_name,
                    response.point_of_contact_first_name,
                ),
                pointOfContactPhone: response.point_of_contact_phone,
                prescriberNpi: response.prescriber_npi,
                practice: response.organization_name,
                practiceNpi: response.practice_npi,
                address1: response.address_1,
                address2: response.address_2,
                city: response.city,
                state: response.state,
                zip: response.postal_code?.trim(),
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving prescriber details.', err);
                }
            },
        }),
        /** Gets list of all Product/Services */
        getAllProductServices: build.query({
            query: () => '/productservices/summaryonly',
            transformResponse: (response) => {
                const productServiceList = (response ?? [])
                    .map((productService) => ({
                        oid: productService.oid,
                        name: productService.description,
                        secondaryData: productService.oid,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const productServiceMap = Object.fromEntries(
                    productServiceList.map(({ oid, name }) => [oid, name]),
                );
                return { productServiceList, productServiceMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving list of products.',
            ),
        }),
        getProductByOid: build.query({
            query: (oid) => `/productservices/${oid}`,
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving product.'),
        }),
        getNdcByGpi: build.query({
            query: (gpi) => ({
                url: '/productservices/ndc',
                params: {
                    gpi,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving NDC information by GPI.',
            ),
        }),
        getPlacesOfService: build.query({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response) =>
                response
                    .map((option) => ({
                        value: option.code,
                        name: option.description,
                        secondaryData: option.code,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'Error retrieving place of service options.',
            ),
        }),
        /** Gets list of configured products for a payer. */
        getConfiguredProductsForPayer: build.query({
            query: ({ segmentId }) => `product_benefit_configs/segments/${segmentId}`,
            transformResponse: (response) =>
                response
                    .map((product) => {
                        const baseProps = {
                            oid: product.product_service_oid,
                            defaultRate: product.default_rate,
                            benefit: product.benefit?.trim(),
                            therapeuticCategory: product.therapeutic_category,
                        };
                        return [
                            {
                                ...baseProps,
                                name: product.brand_product_description,
                                secondaryData: 'Brand',
                                isBrand: true,
                            },
                            ...(product.generic_product_description &&
                            product.brand_product_description?.toLowerCase() !==
                                product.generic_product_description?.toLowerCase()
                                ? [
                                      {
                                          ...baseProps,
                                          name: product.generic_product_description,
                                          secondaryData: 'Generic',
                                          isBrand: false,
                                      },
                                  ]
                                : []),
                        ];
                    })
                    .flat()
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving configured products for health plan.',
            ),
        }),
        /** Gets list of diagnoses for use as select options. */
        getDiagnoses: build.query({
            query: () => 'diagnoses/',
            transformResponse: (response) =>
                (response ?? [])
                    .map((diagnosis) => ({
                        name: diagnosis.description,
                        value: diagnosis.oid,
                        secondaryData: diagnosis.oid,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError('Error retrieving diagnoses.'),
        }),
        getAllTherapyGroups: build.query({
            query: () => '/therapy_groups',
            transformResponse: (response) => {
                const therapyGroupList = (response ?? []).map((therapyGroup) => ({
                    name: therapyGroup.therapy_group_name,
                    description: therapyGroup.description,
                }));
                const therapyGroupMap = Object.fromEntries(
                    therapyGroupList.map(({ name, description }) => [name, description]),
                );
                return { therapyGroupList, therapyGroupMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving therapy groups.',
            ),
        }),
        getBiosimilarConversionProducts: build.query({
            query: (productServiceOid) =>
                `/therapy_groups/by_product_service_oid/${productServiceOid}/transition_products`,
            transformResponse: (response) =>
                (response ?? [])
                    .map((product) => ({
                        value: product.product_service_oid,
                        is_brand: product.is_brand,
                        name: product.is_brand
                            ? product.brand_product_description
                            : product.description,
                        secondaryData: product.is_brand ? 'Brand' : 'Generic',
                        productServiceIds: product.product_service_ids,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: (err) => {
                const isMissingTherapyGroup =
                    err.status === 404 &&
                    err.data?.detail === 'No therapy group found for this product service oid.';

                if (!isMissingTherapyGroup) {
                    handleError(
                        'An error occurred retrieving biosimilar conversion products.',
                        err,
                    );
                }
            },
        }),
        getPriceSources: build.query({
            query: () => 'prices/sources',
            transformResponse: (response) =>
                (response ?? []).map((source) => ({
                    value: source.price_source,
                    name: source.display_name,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving price list sources.',
            ),
        }),
        getChildSupplierNpisByNetwork: build.query({
            query: ({ networkCode }) => `supplier_child_segments/${networkCode}`,
            transformResponse: (response) => response.sort(sortAsc),
            transformErrorResponse: handleRTKQueryError(
                'Error retrieving child npis from network code.',
            ),
        }),
    }),
});

export const {
    useGetPayersQuery,
    useGetPayerSegmentsQuery,
    useGetAllSuppliersQuery,
    useGetAllSuppliersWithChildrenQuery,
    useGetEligibleSuppliersQuery,
    useGetSupplierDetailsQuery,
    useGetPrescriberDetailsQuery,
    useLazyGetEligibleSuppliersQuery,
    useLazyGetPrescriberDetailsQuery,
    useGetAllProductServicesQuery,
    useGetProductByOidQuery,
    useGetPlacesOfServiceQuery,
    useGetConfiguredProductsForPayerQuery,
    useGetDiagnosesQuery,
    useGetAllTherapyGroupsQuery,
    useGetBiosimilarConversionProductsQuery,
    useGetPriceSourcesQuery,
    useGetNdcByGpiQuery,
    useGetChildSupplierNpisByNetworkQuery,
} = referenceApi;
